import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import { NotificationService } from '../../lib/notifications';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  BellIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BriefcaseIcon,
  HeartIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const PushNotifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const isDevUI = (() => {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') return true;
    if (typeof window !== 'undefined') {
      const host = window.location.hostname || '';
      if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') return true;
      if (/^10\./.test(host) || /^192\.168\./.test(host)) return true;
      const m = host.match(/^172\.(\d{1,2})\./);
      if (m) {
        const n = parseInt(m[1], 10);
        if (n >= 16 && n <= 31) return true;
      }
    }
    return false;
  })();

  const [settings, setSettings] = useState({
    enabled: true,
    new_jobs: true,
    job_updates: true,
    application_status: true,
    shift_reminders: true,
    payment_notifications: true,
    promotional: false,
    quiet_hours: {
      enabled: false,
      start_time: '22:00',
      end_time: '08:00'
    }
  });
  const [permissionStatus, setPermissionStatus] = useState('default'); // 'granted', 'denied', 'default'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotificationSettings();
    checkNotificationPermission();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const res = await authApi.get('/users/settings/notifications');
      if (res && res.data) {
        const merged = { ...settings, ...res.data };
        setSettings(merged);
        try { localStorage.setItem('push_settings', JSON.stringify(merged)); } catch (_) {}
      } else {
        try { localStorage.setItem('push_settings', JSON.stringify(settings)); } catch (_) {}
      }
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkNotificationPermission = async () => {
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      try {
        const permission = await Notification.requestPermission();
        setPermissionStatus(permission);
        
        if (permission === 'granted') {
          const newSettings = { ...settings, enabled: true };
          await saveSettings(newSettings);
          toast.success('プッシュ通知を有効にしました');
          
          // テスト通知を送信
          new Notification('バイターズ', {
            body: 'プッシュ通知の設定が完了しました！',
            icon: '/favicon.ico'
          });
        } else {
          toast.error('プッシュ通知が拒否されました');
        }
      } catch (error) {
        console.error('Notification permission error:', error);
        toast.error('プッシュ通知の設定に失敗しました');
      }
    } else {
      toast.error('このブラウザはプッシュ通知をサポートしていません');
    }
  };

  const saveSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      await authApi.put('/users/settings/notifications', newSettings);
      setSettings(newSettings);
      try { localStorage.setItem('push_settings', JSON.stringify(newSettings)); } catch (_) {}
      toast.success('設定を保存しました');
    } catch (error) {
      toast.error('設定の保存に失敗しました');
    }
  };

  const handleToggle = async (key) => {
    if (key === 'enabled' && !settings.enabled) {
      await requestNotificationPermission();
    } else {
      const newSettings = {
        ...settings,
        [key]: !settings[key]
      };
      await saveSettings(newSettings);
    }
  };

  const triggerTest = (type) => {
    const res = NotificationService.trigger(type, { text: 'テスト通知です' }, settings);
    if (res.ok) {
      toast.success('テスト通知を送信しました');
    } else {
      const reason = {
        unsupported: 'ブラウザが対応していません',
        not_granted: '通知の許可が必要です',
        disabled_all: 'プッシュ通知が無効です',
        disabled_type: 'この通知タイプはオフです',
        quiet_hours: 'サイレント時間中です',
      }[res.reason] || '送信できませんでした';
      toast.error(reason);
    }
  };

  const handleQuietHoursToggle = async () => {
    const newSettings = {
      ...settings,
      quiet_hours: {
        ...settings.quiet_hours,
        enabled: !settings.quiet_hours.enabled
      }
    };
    await saveSettings(newSettings);
  };

  const handleTimeChange = async (timeType, value) => {
    const newSettings = {
      ...settings,
      quiet_hours: {
        ...settings.quiet_hours,
        [timeType]: value
      }
    };
    await saveSettings(newSettings);
  };

  const getPermissionIcon = () => {
    switch (permissionStatus) {
      case 'granted':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'denied':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getPermissionText = () => {
    switch (permissionStatus) {
      case 'granted':
        return '許可済み';
      case 'denied':
        return '拒否されています';
      default:
        return '許可が必要です';
    }
  };

  const notificationTypes = [
    {
      key: 'new_jobs',
      label: '新着求人通知',
      description: 'あなたの条件に合った新しい求人が投稿された時',
      icon: BriefcaseIcon,
      color: 'blue'
    },
    {
      key: 'job_updates',
      label: '求人情報の更新',
      description: 'お気に入りの求人の内容が変更された時',
      icon: HeartIcon,
      color: 'pink'
    },
    {
      key: 'application_status',
      label: '応募状況の変更',
      description: '応募した求人の採用・不採用などの状況変更時',
      icon: ExclamationTriangleIcon,
      color: 'yellow'
    },
    {
      key: 'shift_reminders',
      label: 'シフトリマインダー',
      description: '勤務開始の1時間前にお知らせ',
      icon: ClockIcon,
      color: 'purple'
    },
    {
      key: 'payment_notifications',
      label: '報酬の入金通知',
      description: 'ウォレットに報酬が入金された時',
      icon: BellIcon,
      color: 'green'
    },
    {
      key: 'promotional',
      label: 'キャンペーン・お知らせ',
      description: '特別なキャンペーンやサービスの更新情報',
      icon: BellIcon,
      color: 'orange'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">プッシュ通知設定</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 通知許可状態 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center mb-4">
            <BellIcon className="w-6 h-6 mr-3 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">通知の状態</h2>
          </div>
          
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-gray-700">ブラウザの通知許可</span>
            </div>
            <div className="flex items-center">
              {getPermissionIcon()}
              <span className="ml-2 text-sm text-gray-600">{getPermissionText()}</span>
            </div>
          </div>

          {permissionStatus !== 'granted' && (
            <button
              onClick={requestNotificationPermission}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              プッシュ通知を許可する
            </button>
          )}
        </div>

        {/* 基本設定 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">基本設定</h2>
          
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">プッシュ通知</h3>
              <p className="text-sm text-gray-600">すべてのプッシュ通知の有効・無効を切り替えます</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={() => handleToggle('enabled')}
                disabled={permissionStatus === 'denied'}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
            </label>
          </div>
        </div>

        {/* 通知種類 */}
        {settings.enabled && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">通知の種類</h2>
            
            <div className="space-y-4">
              {notificationTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <div key={type.key} className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className={`p-2 rounded-lg mr-4 bg-${type.color}-100`}>
                        <IconComponent className={`w-5 h-5 text-${type.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{type.label}</h3>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {isDevUI && (
                        <button
                          onClick={() => triggerTest(type.key)}
                          disabled={!settings.enabled}
                          className="px-3 py-1 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          テスト
                        </button>
                      )}
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings[type.key]}
                          onChange={() => handleToggle(type.key)}
                          disabled={!settings.enabled}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* サイレント時間 */}
        {settings.enabled && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">サイレント時間</h2>
            
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">サイレント時間を設定</h3>
                <p className="text-sm text-gray-600">指定した時間帯は通知を受信しません</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.quiet_hours.enabled}
                  onChange={handleQuietHoursToggle}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {settings.quiet_hours.enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    開始時間
                  </label>
                  <input
                    type="time"
                    value={settings.quiet_hours.start_time}
                    onChange={(e) => handleTimeChange('start_time', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    終了時間
                  </label>
                  <input
                    type="time"
                    value={settings.quiet_hours.end_time}
                    onChange={(e) => handleTimeChange('end_time', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* テスト通知 */}
        {settings.enabled && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">テスト通知</h2>
            
            <button
              onClick={() => {
                if (Notification.permission === 'granted') {
                  new Notification('バイターズ テスト通知', {
                    body: 'プッシュ通知が正常に動作しています！',
                    icon: '/favicon.ico'
                  });
                  toast.success('テスト通知を送信しました');
                } else {
                  toast.error('通知の許可が必要です');
                }
              }}
              className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
            >
              テスト通知を送信
            </button>
          </div>
        )}

        {/* 注意事項 */}
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-start">
            <InformationCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">プッシュ通知について</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li> ブラウザの設定でも通知のオン・オフができます</li>
                <li> サイレント時間中でも緊急性の高い通知は配信される場合があります</li>
                <li> 通知の内容に個人情報は含まれません</li>
                <li> アプリを閉じていても通知を受信できます</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotifications;